/**
 * EquipmentCategoriesAdmin (ADMIN-EDIT-01) — Settings section: the equipment
 * categories the classifier + Classification Review use. Builtins: rename
 * only (the rules emit their keys — archiving them would orphan real units).
 * Customs: add, rename, regroup, archive/restore. Plain-English groupings:
 * "Main system" (furnace/AC class), "Add-on" (humidifier class), "Other".
 */
import { useState } from "react";
import { Boxes, Check, Pencil, Plus, RotateCcw, Snowflake, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import {
  useEquipmentCategories,
  useAddEquipmentCategory,
  useUpdateEquipmentCategory,
  type EquipmentCategoryRow,
} from "./equipmentCategories";

const GROUP_LABEL: Record<EquipmentCategoryRow["grouping"], string> = {
  system: "Main system",
  auxiliary: "Add-on",
  other: "Other",
};

export function EquipmentCategoriesAdmin() {
  const { data, isLoading } = useEquipmentCategories();
  const add = useAddEquipmentCategory();
  const update = useUpdateEquipmentCategory();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [grouping, setGrouping] = useState<EquipmentCategoryRow["grouping"]>("system");
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");

  const rows = data ?? [];
  const active = rows.filter((r) => r.active);
  const archived = rows.filter((r) => !r.active);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-sky-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
            <Boxes className="h-3.5 w-3.5" />
          </span>
          <span className="text-[13px] font-bold text-slate-800">
            Equipment Categories
          </span>
          <InfoTip label="About equipment categories">
            The AI and the Classification Review label equipment with these. Built-in ones can be renamed but not archived,
            since the classifier still produces them; &ldquo;Holds refrigerant&rdquo; means installers log the charge and what
            they recovered from the old unit.
          </InfoTip>
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 transition-colors hover:text-brand-900"
        >
          <Plus className="h-3 w-3" />
          Add category
        </button>
      </div>

      <div className="px-4 py-3">
        {adding && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate(
                { label, grouping },
                { onSuccess: () => { setLabel(""); setAdding(false); } },
              );
            }}
            className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-2"
          >
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Pool Heater"
              className="min-w-[160px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
            <select
              value={grouping}
              onChange={(e) => setGrouping(e.target.value as EquipmentCategoryRow["grouping"])}
              title="Where it shows on the customer's Equipment card"
              className="rounded-md border border-hairline bg-white px-2 py-1.5 text-sm text-slate-700"
            >
              <option value="system">Main system (furnace/AC class)</option>
              <option value="auxiliary">Add-on (humidifier class)</option>
              <option value="other">Other</option>
            </select>
            <button
              type="submit"
              disabled={!label.trim() || add.isPending}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {add.isPending ? "Saving…" : "Add"}
            </button>
            {add.isError && (
              <span className="text-xs text-red-600">{(add.error as Error).message}</span>
            )}
          </form>
        )}

        {isLoading ? (
          <div className="space-y-1.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-8 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {active.map((c) => (
              <li key={c.key} className="group flex items-center gap-2 py-1.5">
                {editKey === c.key ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      update.mutate(
                        { key: c.key, label: editLabel },
                        { onSuccess: () => setEditKey(null) },
                      );
                    }}
                    className="flex flex-1 items-center gap-2"
                  >
                    <input
                      autoFocus
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      className="flex-1 rounded-md border border-hairline px-2 py-1 text-sm"
                    />
                    <button type="submit" className="rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white">
                      <Check className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => setEditKey(null)} className="p-1 text-slate-400 hover:text-slate-600">
                      <X className="h-3 w-3" />
                    </button>
                  </form>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                      {c.label}
                      {c.builtin && (
                        <span className="ml-1.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-slate-300">
                          built-in
                        </span>
                      )}
                    </span>
                    {!c.builtin && (
                      <select
                        value={c.grouping}
                        onChange={(e) =>
                          update.mutate({ key: c.key, grouping: e.target.value as EquipmentCategoryRow["grouping"] })
                        }
                        title="Where it shows on the customer's Equipment card"
                        className="rounded border border-hairline bg-white px-1.5 py-0.5 text-[11px] text-slate-500"
                      >
                        <option value="system">Main system</option>
                        <option value="auxiliary">Add-on</option>
                        <option value="other">Other</option>
                      </select>
                    )}
                    {c.builtin && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                        {GROUP_LABEL[c.grouping]}
                      </span>
                    )}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!c.uses_refrigerant}
                      onClick={() => update.mutate({ key: c.key, uses_refrigerant: !c.uses_refrigerant })}
                      title={c.uses_refrigerant ? "Installers log refrigerant on this — click to stop asking" : "Installers don't log refrigerant on this — click to ask them"}
                      className={cn(
                        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors",
                        c.uses_refrigerant ? "bg-sky-50 text-sky-700 hover:bg-sky-100" : "text-slate-300 hover:bg-slate-50 hover:text-slate-500",
                      )}
                    >
                      <Snowflake className="h-3 w-3" />
                      {c.uses_refrigerant ? "Holds refrigerant" : "No refrigerant"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditKey(c.key); setEditLabel(c.label); }}
                      title="Rename"
                      className="rounded p-1 text-slate-300 opacity-40 transition-all hover:text-slate-600 group-hover:opacity-100"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    {!c.builtin && (
                      <button
                        type="button"
                        onClick={() => update.mutate({ key: c.key, active: false })}
                        title="Archive — hides it from pickers; existing equipment keeps the label"
                        className="rounded p-1 text-slate-300 opacity-40 transition-all hover:text-red-500 group-hover:opacity-100"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {archived.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Archived
            </p>
            <ul className="space-y-1">
              {archived.map((c) => (
                <li key={c.key} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="min-w-0 flex-1 truncate line-through">{c.label}</span>
                  <button
                    type="button"
                    onClick={() => update.mutate({ key: c.key, active: true })}
                    title="Restore"
                    className={cn("inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50")}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
